import {FC, MouseEvent} from 'react';
import {observer} from 'mobx-react';
import {Accordion, Button, Dropdown, Icon, Input, Popup} from 'semantic-ui-react';
import {Field} from 'apstra-ui-common';
import {filter, first, isNull, last, map, uniqBy} from 'lodash';

import TagsControl from '../../components/TagsControl';
import LinkGroupSize from './LinksGroupSize';
import {Checkbox} from '../../exports';
import useLinksGroupOptions from '../hooks/useLinksGroupOptions';
import {LAG_MODES, LAG_MODES_NAMES, LAG_MODES_TITLES} from '../const';

import Step from '../../cablingMapEditor/store/Step';
import Port from './Port';
import Node from '../store/Node';
import LinksGroup from '../store/LinksGroup';
import {useRackEditorStore} from '../hooks/useRackEditorStore';
import {SpeedPortsCountType} from '../types';

import './LinksGroupForm.less';

export const labelDefaults = {empty: true, circular: false};

type LinksGroupFormProps = {
  linksGroup: LinksGroup;
  freePorts: SpeedPortsCountType[][];
  newLinkRestriction: string | null;
}

export type OptionType = {
  text: string | number | null;
  value: unknown;
  label?: Record<string, any>;
}

const LinksGroupForm: FC<LinksGroupFormProps> = ({linksGroup, freePorts, newLinkRestriction}) => {
  const linksGroupOptions = useLinksGroupForm(linksGroup, freePorts);
  const {
    deleteLinksGroup, availableTags, maxSizePerSpeed, portsAvailable,
    onPortClick, canLinkFirst, canLinkSecond, canLinkBoth, openedLinksGroupId, toggleLinksGroup, fromGeneric,
    trackChanges, toggleIsRail, railIndexOptions, isRailCollapsed, showRailOptions, canClone, cloneLinksGroup,
    options
  } = linksGroupOptions;

  const isActive = linksGroup.id === openedLinksGroupId;
  const maxLinks = portsAvailable + linksGroup.count;
  const railPrefix = linksGroup.isInRail ? <span>{`Rail #${linksGroup.railIndex}:`}</span> : null;

  return (
    <>
      <Accordion.Title className='re-links-group' active={isActive} onClick={toggleLinksGroup}>
        <Icon name='dropdown' />
        {railPrefix}
        <b>{linksGroup.label || '<Empty>'}</b>
        <Port amount={linksGroup.count} speedString={linksGroup.speedString} />
        {canClone && !newLinkRestriction &&
          <Button icon='clone' size='tiny' onClick={cloneLinksGroup} />
        }
        {
          linksGroup.isInRail ?
            <Dropdown
              direction='left'
              options={options}
              trigger={<Button icon='trash' size='tiny' />}
            /> :
            <Button icon='trash' size='tiny' onClick={deleteLinksGroup} />
        }
      </Accordion.Title>
      <Accordion.Content className='re-links-group-content' active={isActive}>
        <Field label='Label' required>
          <Input
            size='small'
            fluid
            value={linksGroup.label}
            onChange={(event, {value}) => trackChanges({label: value})}
            error={!linksGroup.label}
            maxLength={64}
          />
        </Field>
        <Field label='Tags'>
          <TagsControl
            className='tags'
            size='tiny'
            fluid
            value={linksGroup.tags ?? []}
            knownTags={availableTags}
            onChange={(value) => trackChanges({tags: value})}
          />
        </Field>
        <LinkGroupSize
          showRailOptions={!!showRailOptions}
          linksGroup={linksGroup}
          maxSizePerSpeed={maxSizePerSpeed}
          onPortClick={onPortClick}
          toggleIsRail={toggleIsRail}
          railIndexOptions={railIndexOptions}
          trackChanges={trackChanges}
          maxLinks={maxLinks}
          canLinkFirst={!!canLinkFirst}
          canLinkSecond={!!canLinkSecond}
          canLinkBoth={canLinkBoth}
          linksGroupOptions={linksGroupOptions}
        />
        {
          fromGeneric && !linksGroup.isInRail && !isRailCollapsed && !linksGroup.isInRail &&
            <Field label='LAG Mode' className='lag-mode'>
              <div>
                {
                  map(LAG_MODES, (mode) => makeLagCheckbox(mode, linksGroup, trackChanges))
                }
              </div>
            </Field>
        }
      </Accordion.Content>
    </>
  );
};

export default observer(LinksGroupForm);

const useLinksGroupForm = (linksGroup: LinksGroup, freePorts: SpeedPortsCountType[][]) => {
  const {rackStore, selection, portsSpeeds} = useRackEditorStore();
  const {openedLinksGroupId, availableTags, availableRailIndex, supportsRails, isRailCollapsed, isL3Collapsed,
    railsLinksByLeafId} = rackStore;

  const {
    maxSizePerSpeed, toggleHoming, toggleSwitchLink, onPortClick, portsAvailable, homingType,
    fromPorts0, toPorts0, toPorts1, betweenPairs, isOneToPair, fromNodes, toNodes, toPair, trackChanges
  } = useLinksGroupOptions(linksGroup, freePorts);

  const {
    id, speedString, count, isAttachedToFirst, isAttachedToSecond, isDualAttached
  } = linksGroup;

  // Is it possible to clone the links group?
  const canClone = +portsSpeeds[speedString] >= count;

  // Identifies whether isAttachedToFirst can be changed
  const canLinkFirst =
    // Only makes sense for one-to-pair configuration and
    isOneToPair && (
      (
        // if attached to the second and can be reattached to the first
        !isAttachedToFirst || toPorts1 >= count
      ) || isDualAttached
    );

  // Identifies whether isAttachedToSecond can be changed
  const canLinkSecond =
    // Only makes sense for one-to-pair configuration
    isOneToPair && (
      (
        // if attached to the first and can be reattached to the second
        !isAttachedToSecond || toPorts0 >= count
      ) || isDualAttached
    );

  // Identifies whether dual-homing changing is allowed
  const canLinkBoth = (
    (
      // For pair-to-pair configuration
      betweenPairs &&
        (
          // If have cross-links already or
          isDualAttached ||
          // all nodes have enough free ports to create them
          maxSizePerSpeed[speedString] >= count
        )
    ) || (
      // For one-to-pair configuration
      isOneToPair &&
      // when source has enough free ports for dual-homing
      fromPorts0 >= count && (
        // and unset target has enough free ports either
        (!isAttachedToFirst && toPorts0 >= count) ||
        (!isAttachedToSecond && toPorts1 >= count)
      )
    )
  );

  const sourceNode = first<Node>(fromNodes);
  const fromGeneric = sourceNode?.isGeneric;
  const destinationNode = first<Node>(toNodes);
  const toLeaf = destinationNode?.isLeaf && !toPair;

  const railIndexOptions: OptionType[] = [];
  if (fromGeneric && toLeaf) {
    railIndexOptions.push(
      ...uniqBy(
        map(
          filter(
            railsLinksByLeafId[destinationNode?.id],
            ({id, speedString, fromName, fromNode, railIndex}) => (
              // All the links of the same speed but from other generics and generics - same logical device...
              (speedString === linksGroup.speedString && fromName !== linksGroup.fromName &&
                fromNode?.logicalDevice === linksGroup.fromNode?.logicalDevice) ||
              // or itself if in the rail already
              (id === linksGroup.id && !isNull(railIndex) && railIndex >= 0)
            )
          ),
          ({railIndex}) => ({
            text: railIndex,
            value: railIndex,
            label: {className: `rail${railIndex}`, ...labelDefaults}
          })
        ),
        'value'
      ),
      {text: 'New Index', value: availableRailIndex, label: labelDefaults},
    );
  }

  // Only show rail options for the links from generic to lag ...
  const showRailOptions = fromGeneric && toLeaf &&
    // when rails support is on ...
    supportsRails &&
    // for L3Clos and Rail Collapsed designs ...
    !isL3Collapsed &&
    // and when there is no LAG
    linksGroup.lagMode === LAG_MODES.NO_LAG;

  const toggleIsRail = () => {
    if (linksGroup.isInRail) {
      // Remove from the rail
      trackChanges({railIndex: null});
    } else {
      // Add to a rail
      trackChanges({railIndex: last(railIndexOptions)?.value, count: 1, lagMode: LAG_MODES.NO_LAG});
    }
  };

  const toggleLinksGroup = () => rackStore.toggleLinker(id, selection);

  const deleteLinksGroups = (event: MouseEvent<HTMLButtonElement | HTMLDivElement>, linksGroups: LinksGroup[]) => {
    event.stopPropagation();
    // If opened links group gets deleted - its section must be collapsed
    if (id === openedLinksGroupId) rackStore.toggleLinker(undefined, selection);
    // Register deletion in history
    rackStore.changes.register(map(linksGroups, (linksGroup) => Step.deletion(linksGroup)));
    // Delete the links group
    rackStore.deleteLinksGroups(map(linksGroups, 'id'));
  };

  const deleteLinksGroup = (event: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    deleteLinksGroups(event, [linksGroup]);
  };

  const deleteRail = (event: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    deleteLinksGroups(
      event,
      filter(rackStore.railsLinksByLeafId[destinationNode!.id], {railIndex: linksGroup.railIndex})
    );
  };

  const cloneLinksGroup = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const newLinksGroup = linksGroup.clone();
    if (newLinksGroup.isInRail) {
      newLinksGroup.railIndex = availableRailIndex;
    }
    // Register creation in history
    rackStore.changes.register([Step.creation(newLinksGroup)]);
    // Add the links group
    rackStore.addLinksGroup(newLinksGroup);
  };

  const options = [
    {key: 'link', icon: 'unlink', text: 'Delete Link', value: 'edit', onClick: deleteLinksGroup},
    {key: 'rail', icon: 'trash', text: 'Delete Rail', value: 'delete', onClick: deleteRail}
  ];

  return {availableTags, homingType, canLinkFirst, canLinkSecond, canLinkBoth, toggleSwitchLink, toggleHoming,
    maxSizePerSpeed, onPortClick, portsAvailable, fromGeneric, openedLinksGroupId,
    toggleLinksGroup, deleteLinksGroup, trackChanges, toggleIsRail, railIndexOptions, isRailCollapsed,
    showRailOptions, canClone, cloneLinksGroup, options};
};

const bracketsExpr = new RegExp(/^(.+) \((.+)\)$/, 'i');

const makeLagCheckbox = (mode, linksGroup, trackChanges) => {
  const tooltip = LAG_MODES_TITLES[mode];

  const label = LAG_MODES_NAMES[mode];
  const parts = label.match(bracketsExpr);
  const labelControl = (
    <>
      <span>
        {parts ? parts[1] : label}
        {
          parts &&
            <>
              <br />
              <small>{`${parts[2]}`}</small>
            </>
        }
      </span>
      {
        tooltip &&
          <Popup
            trigger={<Icon name='info circle' />}
            content={tooltip}
            offset={[-12, 0]}
          />
      }
    </>
  );

  const disabled = mode === LAG_MODES.NO_LAG && (linksGroup.count > 1 || linksGroup.isDualAttached);

  return (
    <Checkbox
      key={mode}
      value={mode}
      radio
      label={labelControl}
      name='lagMode'
      checked={linksGroup.lagMode === mode}
      onClick={disabled ? undefined : () => trackChanges({lagMode: mode, railIndex: null})}
      disabled={disabled}
    />
  );
};
